import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Mail, Shield, HelpCircle, AlertTriangle, Loader2 } from 'lucide-react';
import AttachmentCard from './chat/AttachmentCard';
import { CHAT_TOKENS } from './chat/chatTokens';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LegacyTicketDrawer — read-only drawer that renders the imported PHP
 * conversation for one synthesised legacy ticket. Used from:
 *   - Admin Tickets (TicketsList) when source=legacy
 *   - Customer Tickets (same component, isAdmin=false)
 *   - Admin / Customer Order Detail (inline mode without slideout — see
 *     `inline` prop)
 *
 * Sender-type bubbles, attachment paths with "storage not connected"
 * label, and `legacy type 1` badges mirror the Tools → Tickets Browser
 * so the operator sees the exact same data in either entry point.
 *
 * Backend endpoints used:
 *   GET /api/admin/legacy/tickets/{id}/messages       (admin)
 *   GET /api/customer/legacy/tickets/{id}/messages    (customer)
 */
export default function LegacyTicketDrawer({
  legacyTicketId, token, isAdmin, onClose, inline = false,
}) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({});
  const [senderFilter, setSenderFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!legacyTicketId) return;
    let active = true;
    setLoading(true);
    const url = isAdmin
      ? `${API_URL}/api/admin/legacy/tickets/${encodeURIComponent(legacyTicketId)}/messages${senderFilter !== 'all' ? `?sender_type=${senderFilter}` : ''}`
      : `${API_URL}/api/customer/legacy/tickets/${encodeURIComponent(legacyTicketId)}/messages`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!active) return;
        // Admin endpoint also returns the ticket inline via /tickets?...
        // but for the customer endpoint we get `ticket` in the response.
        setTicket(data.ticket || null);
        let msgs = data.items || [];
        // Customer endpoint has no server-side filter — apply client-side
        if (!isAdmin && senderFilter !== 'all') {
          msgs = msgs.filter((m) => m.sender_type === senderFilter);
        }
        setMessages(msgs);
        setCounts(data.counts || {});
      })
      .catch((e) => { if (active) toast.error(`Legacy ticket: ${e.message}`); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [legacyTicketId, senderFilter, isAdmin, token]);

  if (!legacyTicketId) return null;

  const Body = (
    <>
      {/* Sender-type chips (shared with Tools → Tickets Browser) */}
      <div className="flex flex-wrap gap-2 mb-3 text-xs items-center" data-testid="legacy-drawer-sender-chips">
        <span className="text-slate-500 font-semibold">Sender:</span>
        {[
          ['all',              'All',           null],
          ['customer',         isAdmin ? 'Customer' : 'You', counts.customer],
          ['admin',            isAdmin ? 'Admin'    : 'AUTO-REMAP', counts.admin],
          ['staff_or_unknown', isAdmin ? 'Staff/Unknown' : 'Staff', counts.staff_or_unknown],
        ].map(([k, label, count]) => (
          <button
            key={k}
            onClick={() => setSenderFilter(k)}
            className={`px-2 py-0.5 rounded border ${
              senderFilter === k
                ? 'bg-cyan-600 text-white border-cyan-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
            data-testid={`legacy-drawer-sender-${k}`}
          >
            {label}{count != null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 italic p-4"><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading legacy conversation...</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-slate-500 italic p-4">No legacy messages match this filter.</p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="legacy-drawer-messages">
          {messages.map((m) => <LegacyMessageBubble key={m.id} m={m} isAdmin={isAdmin} token={token} />)}
        </div>
      )}

      <p className="mt-3 pt-2 border-t border-slate-200 text-[11px] text-slate-500 italic">
        Read-only legacy conversation imported from the previous platform. New replies are not posted here — open a new ticket if you need to follow up.
      </p>
    </>
  );

  // Inline mode (embedded in OrderDetailDrawer) — render without slideout.
  if (inline) {
    return <div data-testid="legacy-ticket-inline">{Body}</div>;
  }

  return (
    <div className="fixed inset-0 z-[60]" data-testid="legacy-ticket-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full md:w-[640px] bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              {ticket?.subject || 'Legacy ticket'}
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-300 uppercase">Legacy</span>
              {ticket?.orphan && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 uppercase flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Orphan
                </span>
              )}
            </h2>
            {ticket && (
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                {ticket.legacy_order_id && <>legacy_order_id <code className="bg-slate-100 px-1 rounded">{ticket.legacy_order_id}</code> · </>}
                {ticket.message_count != null && <>{ticket.message_count} messages</>}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="legacy-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{Body}</div>
      </div>
    </div>
  );
}

function LegacyMessageBubble({ m, isAdmin, token }) {
  const sender = m.sender_type || 'unknown';
  const Icon = sender === 'customer' ? Mail : sender === 'admin' ? Shield : HelpCircle;
  // From the ADMIN perspective, admin=own (mint, right); from the CUSTOMER
  // perspective, customer=own. Match the semantics of the main chat drawer.
  const onRight = isAdmin ? (sender === 'admin') : (sender === 'customer');
  const isNeutral = sender !== 'admin' && sender !== 'customer';
  const bubbleStyle = isNeutral
    ? { background: '#f3f4f6', border: '1px solid #e5e7eb' }
    : onRight
      ? { background: CHAT_TOKENS.ownBg }
      : { background: CHAT_TOKENS.otherBg, border: `1px solid ${CHAT_TOKENS.otherBorder}` };
  const senderLabel = isAdmin
    ? sender.replace('_', '/')
    : sender === 'customer' ? 'You'
    : sender === 'admin'    ? 'AUTO-REMAP'
    : 'Staff';

  const [downloading, setDownloading] = useState(false);
  const downloadViaProxy = async () => {
    if (!m.legacy_message_id) return;
    setDownloading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/legacy-files/file_message/${encodeURIComponent(m.legacy_message_id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
        if (detail.code === 'legacy_file_missing' || r.status === 404) {
          toast.error(isAdmin ? 'Legacy file is not available — file not found in old system.' : 'Attachment unavailable.');
        } else if (detail.code === 'forbidden' || r.status === 403) {
          toast.error('This attachment does not belong to your account.');
        } else {
          toast.error(`Download failed: ${detail.message || j?.detail || `HTTP ${r.status}`}`);
        }
        return;
      }
      const blob = await r.blob();
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      if (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/xml') {
        toast.error('Legacy attachment is temporarily unavailable. Please retry in a moment.');
        return;
      }
      const cd = r.headers.get('content-disposition') || '';
      const mm = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
      const name = mm ? decodeURIComponent(mm[1])
                      : (m.attachment_path || '').split('/').pop() || `attachment-${m.legacy_message_id}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(`Download failed: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`flex ${onRight ? 'justify-end' : isNeutral ? 'justify-center' : 'justify-start'}`} data-testid={`legacy-msg-${m.id}`}>
      <div
        className={`max-w-[82%] ${onRight ? 'rounded-[12px] rounded-br-[4px]' : isNeutral ? 'rounded-lg' : 'rounded-[12px] rounded-bl-[4px]'}`}
        style={{ ...bubbleStyle, color: CHAT_TOKENS.text, padding: '8px 12px 6px', boxShadow: '0 1px 1px rgba(0,0,0,0.04)' }}
      >
      <div className="flex items-center justify-between gap-3" style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
        <span className="flex items-center gap-1.5" style={{ color: onRight ? CHAT_TOKENS.authorOwn : CHAT_TOKENS.authorOther }}>
          <Icon className="w-3 h-3" /> {senderLabel}
          {isAdmin && m.legacy_sender_id != null && (
            <span className="opacity-60 font-mono" style={{ color: CHAT_TOKENS.metaFaint }}>· id={m.legacy_sender_id}</span>
          )}
          {m.legacy_type_id === 1 && (
            <span className="px-1 py-0.5 rounded bg-violet-100 text-violet-700 uppercase tracking-wider" style={{ fontSize: 9 }}>legacy type 1</span>
          )}
        </span>
        <span className="font-mono" style={{ fontSize: 10, color: CHAT_TOKENS.meta }}>
          {(m.legacy_created_at || '').slice(0, 19).replace('T', ' ')}
        </span>
      </div>
      <div className="whitespace-pre-wrap break-words" style={{ fontSize: 13, lineHeight: 1.45, color: CHAT_TOKENS.text }}>
        {m.message_text || <em className="opacity-50">(empty)</em>}
      </div>
      {m.attachment_path && (
        <AttachmentCard
          filename={isAdmin ? m.attachment_path : (m.attachment_path || '').split('/').pop()}
          fullTitle={isAdmin ? m.attachment_path : ((m.attachment_path || '').split('/').pop() || 'attachment')}
          ext={((m.attachment_path || '').split('.').pop() || '').toLowerCase()}
          uploadedAt={m.legacy_created_at}
          legacy
          downloadCount={isAdmin ? (m.downloads ?? 0) : null}
          onCopyPath={isAdmin ? () => {
            navigator.clipboard?.writeText(m.attachment_path);
            toast.success('Legacy path copied');
          } : null}
          downloadDisabled={downloading || !m.legacy_message_id}
          onDownload={m.legacy_message_id ? downloadViaProxy : undefined}
          testid={`legacy-drawer-msg-download-${m.legacy_message_id}`}
        />
      )}
      </div>
    </div>
  );
}
